import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { faPlay, faStop } from '@fortawesome/free-solid-svg-icons';
import { faCircle, faEllipsis, faPause } from '@fortawesome/pro-solid-svg-icons';
import * as svgIcons from '@progress/kendo-svg-icons';
import { filter, take } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { BaseCalendarModel } from '../../../../core/models/calendar/base-calendar.model';
import { SharedTimerHandlerService } from '../../../../core/services/features/time-tracking/shared-timer-handler.service';
import {
  SharedTrackerHandlerService,
  TrackerStatus,
} from '../../../../core/services/features/time-tracking/shared-tracker-handler.service';
import { ProjectsHttpService } from '../../../../core/state/projects/projects-http.service';
import { TimeUnitOptionLiterals } from '../../../../core/text-db/units.text';
import { DropdownOption } from '../../../../shared/data-types/dropdown-option';
import { TimeUnits } from '../../../../shared/data-types/unit-types';
import { CalendarEvents, CalendarViews } from '../../calendar-types';
import { CalendarInteractionService } from '../../interaction/calendar-interaction.service';
import { CalendarServiceHelper } from '../../services/calendar-service-helper';
import { CalendarService } from '../../services/calendar.service';
import { DateTimeHelper } from '../../util/date-time-helper';
import { ActiveTimeBlockTimerService } from '../time-block/active/active-time-block-timer.service';
import { CalendarControlBarService } from './calendar-control-bar.service';

@Component({
  selector: 'app-shive-calendar-control-bar',
  templateUrl: './calendar-control-bar.component.html',
  styleUrls: ['./calendar-control-bar.component.scss'],
  standalone: false,
})
export class CalendarControlBarComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() calendarViewMode: CalendarViews;

  // Signals
  // timerActiveTimeBlockId === null => loading
  // timerActiveTimeBlockId < 0 => no active time block
  // timerActiveTimeBlockId >= 0 => active time block exists
  public timerActiveTimeBlockId = signal<number | null>(null);
  public trackerStatus = signal<TrackerStatus>(TrackerStatus.None);

  // Constants
  public chevronLeftIcon = svgIcons.chevronLeftIcon;
  public chevronRightIcon = svgIcons.chevronRightIcon;
  public weekIndex: number;
  public trackerActive: boolean | null = null;
  public CalendarView = CalendarViews;
  public calendarModel: BaseCalendarModel;
  public accumulatedTimeMap = new Map<string, number>();
  public buttonDisabled = true;
  public viewOptions: readonly DropdownOption[] = [
    { text: TimeUnitOptionLiterals.get(TimeUnits.Day), value: CalendarViews.DayGrid },
    { text: TimeUnitOptionLiterals.get(TimeUnits.Week), value: CalendarViews.WeekGrid },
    { text: TimeUnitOptionLiterals.get(TimeUnits.Month), value: CalendarViews.MonthGrid },
  ];
  public readonly faPlay = faPlay;
  public readonly faStop = faStop;

  // Constants
  public readonly faCircle = faCircle;
  public readonly faPause = faPause;
  public readonly faEllipsis = faEllipsis;
  public readonly TrackerStatus = TrackerStatus;
  public readonly String = String;

  private readonly subs = new SubSink();

  constructor(
    public calendarInteractionService: CalendarInteractionService,
    public readonly calendarService: CalendarService,
    public readonly sharedTrackerHandlerService: SharedTrackerHandlerService,
    public readonly sharedTimerHandlerService: SharedTimerHandlerService,
    public readonly activeTimeBlockTimerService: ActiveTimeBlockTimerService,
    public readonly controlBarService: CalendarControlBarService,
    public readonly projectsHttpService: ProjectsHttpService,
    private readonly cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.initCalendarEvents();
    this.initTimeTrackingEvents();
  }

  ngAfterViewInit(): void {
    this.controlBarService.activeCalendarView = this.calendarService.model.calendarViewMode;
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
  private initCalendarEvents(): void {
    const targetEvents = [
      CalendarEvents.Start,
      CalendarEvents.SwitchedCalendarViewMode,
      CalendarEvents.ChangedStartEndDates,
      CalendarEvents.CalculatedWorkingTimeDurations,
      CalendarEvents.UpdatedShiveTrackerStatus,
    ];

    const callback = (
      calendarModel: BaseCalendarModel,
      calendarEvents: CalendarEvents[],
      meta: Map<string, number>,
    ): void => {
      // Calculate work time durations.
      if (calendarEvents.includes(CalendarEvents.CalculatedWorkingTimeDurations)) {
        this.accumulatedTimeMap = meta;
        this.cd.detectChanges();
        return;
      }

      // Toggle start / stop button visibility depending on tracker status
      if (calendarEvents.includes(CalendarEvents.UpdatedShiveTrackerStatus)) {
        this.trackerActive = !!calendarModel.shiveTrackerActive;
        this.cd.detectChanges();
      }

      // Calculate week index.
      this.calendarModel = calendarModel;
      if (
        calendarModel.calendarViewMode === CalendarViews.DayGrid ||
        calendarModel.calendarViewMode === CalendarViews.WeekGrid
      ) {
        this.weekIndex = DateTimeHelper.weekNumber(
          calendarModel.calendarProperties.visibleStartDate,
        );
      }
    };

    this.subs.sink = CalendarServiceHelper.calendarModelUpdated(
      this.calendarService,
      callback,
      targetEvents,
    );
  }

  private initTimeTrackingEvents() {
    this.setTimeTrackingButtonState();

    this.subs.sink = this.sharedTimerHandlerService.timerActiveTBChanged$.subscribe(
      (timerActiveTimeBlockId) => {
        this.timerActiveTimeBlockId.set(timerActiveTimeBlockId);
        this.cd.detectChanges();
      },
    );

    this.subs.sink = this.sharedTrackerHandlerService.trackerStatus$
      .pipe(filter((status) => status !== TrackerStatus.None))
      .subscribe((trackerStatus) => {
        this.trackerStatus.set(trackerStatus);
        this.cd.detectChanges();
      });
  }

  private setTimeTrackingButtonState() {
    this.projectsHttpService
      .getProjects()
      .pipe(take(1))
      .subscribe((projects) => {
        this.buttonDisabled = projects.length === 0;
        this.cd.detectChanges();
      });
  }
}
